import React, { useState, useEffect, useRef } from 'react';
import useResponsive from '../utils/useResponsive';
import { formatearMoneda } from '../utils/formatMoney';
import SeguimientoClienteDetalle from './SeguimientoClienteDetalle';
import FiltroUsuarios from './FiltroUsuarios';
import { useFiltroUsuarios } from '../filtroUsuarios';
import { getUsuarioActual } from '../apiAuth';

const API = '/api';

const money = (v) => v ? `$${formatearMoneda(v)}` : '—';

const SeguimientoClientes = ({ onVolverDashboard, onVerReportes, onVerNotificaciones, onVerHistorico }) => {
  const { isMobile } = useResponsive();
  const [view, setView] = useState('lista');
  const [clientes, setClientes] = useState([]);
  const [clienteId, setClienteId] = useState(null);
  const esAdminPlus = getUsuarioActual()?.rol === 'administrador_plus';
  const filtroUsuariosKey = useFiltroUsuarios().join(',');
  const [filtroNombre, setFiltroNombre] = useState('');
  const [filtroStatus, setFiltroStatus] = useState([]); // [] = todos
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [filtroBancos, setFiltroBancos] = useState([]); // [] = todos
  const [bancosMenuOpen, setBancosMenuOpen] = useState(false);

  // Mide el alto del bloque título+filtros y lo publica como variable CSS,
  // para que el encabezado azul de la tabla se pegue justo debajo de él
  // (mismo patrón que --app-header-h en App.js).
  const cabeceraRef = useRef(null);
  const [cabeceraH, setCabeceraH] = useState(96);
  useEffect(() => {
    if (view !== 'lista') return;
    const el = cabeceraRef.current;
    if (!el) return;
    const medir = () => setCabeceraH(el.offsetHeight);
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    window.addEventListener('resize', medir);
    return () => { ro.disconnect(); window.removeEventListener('resize', medir); };
  }, [isMobile, view]);

  // Mide el alto real de la primera fila del encabezado (en vez de asumir un
  // valor fijo): si algún título largo ("Banco en Formalización", etc.) hace
  // que la fila necesite más espacio del previsto, la segunda fila (Int/Eval/
  // Aut/Rech) debe seguir pegándose justo debajo, sin dejar un hueco blanco.
  const fila1Ref = useRef(null);
  const [fila1H, setFila1H] = useState(30);
  useEffect(() => {
    if (view !== 'lista') return;
    const el = fila1Ref.current;
    if (!el) return;
    const medir = () => setFila1H(el.offsetHeight);
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    window.addEventListener('resize', medir);
    return () => { ro.disconnect(); window.removeEventListener('resize', medir); };
  }, [isMobile, view]);

  const topFila1 = `calc(var(--app-header-h, 120px) + ${cabeceraH}px)`;
  const topFila2 = `calc(var(--app-header-h, 120px) + ${cabeceraH}px + ${fila1H}px)`;

  const st = {
    container: { padding: isMobile ? 12 : 24, maxWidth: 1500, margin: '0 auto' },
    // display:flow-root evita que el marginBottom del último hijo (filtrosRow)
    // "escape" del contenedor (colapso de márgenes), para que offsetHeight
    // mida exactamente el alto real y el thead se pegue justo debajo, sin quedar
    // tapado por este bloque.
    cabeceraSticky: { position: 'sticky', top: 'var(--app-header-h, 120px)', backgroundColor: '#fff', zIndex: 4, paddingTop: 4, display: 'flow-root' },
    header: { marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
    btnPrimary: { padding: '8px 16px', backgroundColor: '#1a3a6e', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13.5, fontWeight: '600' },
    headerBtns: { display: 'flex', gap: 8 },
    filtrosRow: { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #e5e7eb' },
    tableWrap: { border: '1px solid #e5e7eb', borderRadius: 6 },
    table: { width: '100%', borderCollapse: 'collapse', minWidth: 1200 },
    th: { backgroundColor: '#1a3a6e', color: 'white', padding: '6px 10px', textAlign: 'left', fontSize: 12.5, position: 'sticky', top: topFila1, zIndex: 3 },
    thNarrow: { backgroundColor: '#1a3a6e', color: 'white', padding: '4px', textAlign: 'center', fontSize: 12.5, width: 42, position: 'sticky', top: topFila2, zIndex: 2 },
    td: { padding: '5px 10px', borderBottom: '1px solid #e5e7eb', fontSize: 13 },
    tdNarrow: { padding: '5px 4px', borderBottom: '1px solid #e5e7eb', fontSize: 13, textAlign: 'center', width: 42 },
    tdSub: { color: '#6b7280', fontSize: 12 },
    verBtn: { padding: '5px 12px', backgroundColor: '#1a3a6e', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12.5 },
    buscador: { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, width: isMobile ? '100%' : 260 },
    // Filtro multi-status
    statusBox: { position: 'relative' },
    statusBtn: { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, width: isMobile ? '100%' : 240, backgroundColor: 'white', cursor: 'pointer', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
    statusMenu: { position: 'absolute', top: '100%', left: 0, marginTop: 4, backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 20, minWidth: isMobile ? '100%' : 240, maxHeight: 320, overflowY: 'auto', padding: 6 },
    statusItem: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', fontSize: 13.5, cursor: 'pointer', borderRadius: 4 },
    statusDivider: { borderTop: '1px solid #e5e7eb', margin: '4px 0' },
  };

  useEffect(() => {
    fetch(`${API}/catalogos/cat_status_credito`).then(r => r.json()).then(setStatusOptions).catch(() => {});
  }, []);

  // Se recarga también al cambiar el filtro de usuarios del Administrador Plus.
  useEffect(() => { cargar(); }, [filtroUsuariosKey]);

  const cargar = () => {
    fetch(`${API}/seguimiento-clientes`).then(r => r.json()).then(setClientes).catch(() => {});
  };

  const toggleStatus = (nombre) => {
    setFiltroStatus(prev => prev.includes(nombre) ? prev.filter(x => x !== nombre) : [...prev, nombre]);
  };

  const toggleBanco = (banco) => {
    setFiltroBancos(prev => prev.includes(banco) ? prev.filter(x => x !== banco) : [...prev, banco]);
  };

  // Opciones del filtro "Bancos Integrados": los bancos que realmente aparecen
  // en esa columna del listado (sin catálogo fijo, así un banco nuevo sale solo).
  const bancosOptions = [...new Set(clientes.flatMap(c => c.bancos_ingresados_detalle || []))].sort();

  const resumenBancos = filtroBancos.length === 0
    ? 'Todos los bancos integrados'
    : filtroBancos.length === 1
      ? filtroBancos[0]
      : `${filtroBancos.length} bancos seleccionados`;

  const resumenStatus = filtroStatus.length === 0
    ? 'Todos los status'
    : filtroStatus.length === 1
      ? filtroStatus[0]
      : `${filtroStatus.length} status seleccionados`;

  if (view === 'detalle' && clienteId) {
    return (
      <SeguimientoClienteDetalle
        prospectoId={clienteId}
        onVolver={() => { setView('lista'); setClienteId(null); cargar(); }}
      />
    );
  }

  // Status y Banco se evalúan por banco (no contra el status_actual general):
  // el cliente aparece si ALGUNO de sus bancos cumple a la vez el banco y el
  // status seleccionados. Sin selección en un filtro, ese filtro no restringe.
  const cumpleBancoYStatus = (c) => {
    if (filtroStatus.length === 0 && filtroBancos.length === 0) return true;
    return (c.bancos_status_detalle || []).some(b =>
      (filtroBancos.length === 0 || filtroBancos.includes(b.banco))
      && (filtroStatus.length === 0 || filtroStatus.includes(b.status))
    );
  };

  const filtrados = clientes.filter(c =>
    (c.nombre_cliente || '').toLowerCase().includes(filtroNombre.trim().toLowerCase())
    && cumpleBancoYStatus(c)
  );

  return (
    <div style={st.container}>
      <div ref={cabeceraRef} style={st.cabeceraSticky}>
      <div style={st.header}>
        <h2 style={{ fontSize: 24, fontWeight: 'bold', margin: 0 }}>🤝 CRM Hipotecas</h2>
        <div style={st.headerBtns}>
          {onVerReportes && (
            <button style={st.btnPrimary} onClick={onVerReportes}>📊 Reportes</button>
          )}
          {onVerNotificaciones && (
            <button style={st.btnPrimary} onClick={onVerNotificaciones}>🔔 Notificaciones</button>
          )}
          {onVerHistorico && (
            <button style={st.btnPrimary} onClick={onVerHistorico}>📜 Histórico</button>
          )}
          {onVolverDashboard && (
            <button style={st.btnPrimary} onClick={onVolverDashboard}>Dashboard</button>
          )}
        </div>
      </div>
      <div style={st.filtrosRow}>
        <input
          placeholder="Buscar por nombre de cliente..."
          value={filtroNombre}
          onChange={e => setFiltroNombre(e.target.value)}
          style={st.buscador}
        />
        <div style={st.statusBox}>
          <button type="button" style={st.statusBtn} onClick={() => setStatusMenuOpen(o => !o)}>
            <span>{resumenStatus}</span>
            <span style={{ color: '#6b7280' }}>▾</span>
          </button>
          {statusMenuOpen && (
            <>
              <div onClick={() => setStatusMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 15 }} />
              <div style={st.statusMenu}>
                <label style={st.statusItem}>
                  <input type="checkbox" checked={filtroStatus.length === 0} onChange={() => setFiltroStatus([])} />
                  <strong>Todos</strong>
                </label>
                <div style={st.statusDivider} />
                {statusOptions.map(s => (
                  <label key={s.id} style={st.statusItem}>
                    <input type="checkbox" checked={filtroStatus.includes(s.nombre)} onChange={() => toggleStatus(s.nombre)} />
                    {s.nombre}
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
        <div style={st.statusBox}>
          <button type="button" style={st.statusBtn} onClick={() => setBancosMenuOpen(o => !o)}>
            <span>{resumenBancos}</span>
            <span style={{ color: '#6b7280' }}>▾</span>
          </button>
          {bancosMenuOpen && (
            <>
              <div onClick={() => setBancosMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 15 }} />
              <div style={st.statusMenu}>
                <label style={st.statusItem}>
                  <input type="checkbox" checked={filtroBancos.length === 0} onChange={() => setFiltroBancos([])} />
                  <strong>Todos</strong>
                </label>
                <div style={st.statusDivider} />
                {bancosOptions.map(b => (
                  <label key={b} style={st.statusItem}>
                    <input type="checkbox" checked={filtroBancos.includes(b)} onChange={() => toggleBanco(b)} />
                    {b}
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
        {esAdminPlus && <FiltroUsuarios etiqueta={false} ancho={260} />}
      </div>
      </div>
      <div style={st.tableWrap}>
        <table style={st.table}>
          <thead>
            <tr>
              <th style={st.th} rowSpan={2}>ID</th>
              <th style={st.th} rowSpan={2}>Nombre</th>
              <th style={st.th} rowSpan={2}>Status Actual</th>
              <th style={st.th} rowSpan={2}>Bancos Integrados</th>
              <th style={st.th} rowSpan={2}>Tipo Crédito</th>
              <th style={st.th} rowSpan={2}>Crédito Solicitado</th>
              <th style={st.th} rowSpan={2}>Crédito Autorizado</th>
              <th style={st.th} rowSpan={2}>Banco en Formalización</th>
              <th ref={fila1Ref} style={{ ...st.th, textAlign: 'center' }} colSpan={4}>Bancos</th>
              <th style={{ ...st.th, textAlign: 'center' }} rowSpan={2}>Acciones</th>
            </tr>
            <tr>
              <th style={st.thNarrow}>Int</th>
              <th style={st.thNarrow}>Eval</th>
              <th style={st.thNarrow}>Aut</th>
              <th style={st.thNarrow}>Rech</th>
            </tr>
          </thead>
          <tbody>
            {filtrados.map((c, i) => (
              <tr key={c.prospecto_id} style={{ backgroundColor: i % 2 ? 'white' : '#f9fafb' }}>
                <td style={st.td}>{c.prospecto_id}</td>
                <td style={st.td}>
                  {(c.nombre_cliente || '').toUpperCase()}
                  {esAdminPlus && <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 2 }}>👤 {c.usuario_nombre || '-'}</div>}
                </td>
                <td style={st.td}>{c.status_actual || '—'}</td>
                <td style={st.td}>
                  {(c.bancos_ingresados_detalle || []).length
                    ? c.bancos_ingresados_detalle.map(b => <div key={b}>{b}</div>)
                    : '—'}
                </td>
                <td style={st.td}>{c.tipo_credito || '—'}</td>
                <td style={st.td}>{money(c.credito_solicitado)}</td>
                <td style={st.td}>
                  {(c.bancos_autorizados_detalle || []).length
                    ? c.bancos_autorizados_detalle.map(b => (
                        <div key={b.banco} style={{ display: 'flex', gap: 6, alignItems: 'baseline', marginBottom: 4 }}>
                          <span>{money(b.monto_autorizado)}</span>
                          <span style={st.tdSub}>{b.banco}</span>
                        </div>
                      ))
                    : '—'}
                </td>
                <td style={st.td}>
                  {c.banco_formalizacion
                    ? <>
                        <div>{c.banco_formalizacion}</div>
                        <div style={st.tdSub}>{money(c.monto_formalizacion)}</div>
                      </>
                    : '—'}
                </td>
                <td style={st.tdNarrow}>{c.bancos_integrados_count}</td>
                <td style={st.tdNarrow}>{c.bancos_ingresados_count}</td>
                <td style={st.tdNarrow}>{c.bancos_autorizados_count}</td>
                <td style={st.tdNarrow}>{c.bancos_rechazados_count}</td>
                <td style={{ ...st.td, textAlign: 'center' }}>
                  <button style={st.verBtn} onClick={() => { setClienteId(c.prospecto_id); setView('detalle'); }}>Ver</button>
                </td>
              </tr>
            ))}
            {filtrados.length === 0 && (
              <tr><td style={st.td} colSpan={13}>Sin clientes.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SeguimientoClientes;
